import type { ListBandProps } from './ListColumn';

type InvoiceStatus = 'DRAFT' | 'SENT' | 'PROCESSING' | 'PAID' | 'ARCHIVED';

export type PaymentDisplayKind = 'PAID' | 'PENDING' | 'UNPAID';

export type InvoicePaymentFields = {
  status?: string | null;
  isCashPayment?: boolean | null;
  paymentProvider?: string | null;
  squarePaymentStatus?: string | null;
  roadsyncPaymentStatus?: string | null;
};

export type InvoicePaymentDisplay = {
  kind: PaymentDisplayKind;
  label: string;
  badgeClass: string;
};

const BADGE_CLASSES: Record<PaymentDisplayKind, string> = {
  PAID: 'bg-success-subtle text-success-subtle-foreground',
  PENDING: 'bg-warning-subtle text-warning-subtle-foreground',
  UNPAID: 'bg-muted text-foreground',
};

/**
 * Derive booth-facing payment display from invoice status + provider fields.
 * Mirrors backend resolvePublicPaymentStatusDetail.
 */
export function resolveInvoicePaymentDisplay(
  invoice: InvoicePaymentFields | null | undefined
): InvoicePaymentDisplay {
  if (!invoice) {
    return {
      kind: 'UNPAID',
      label: 'Unpaid',
      badgeClass: BADGE_CLASSES.UNPAID,
    };
  }

  const isPaid =
    invoice.status === 'PAID' ||
    invoice.squarePaymentStatus === 'COMPLETED' ||
    invoice.roadsyncPaymentStatus === 'COMPLETED';

  if (isPaid) {
    return {
      kind: 'PAID',
      label: 'Paid',
      badgeClass: BADGE_CLASSES.PAID,
    };
  }

  const isPending =
    invoice.squarePaymentStatus === 'PENDING' ||
    invoice.roadsyncPaymentStatus === 'PENDING' ||
    invoice.status === 'PROCESSING';

  if (isPending) {
    return {
      kind: 'PENDING',
      label: 'Payment pending',
      badgeClass: BADGE_CLASSES.PENDING,
    };
  }

  return {
    kind: 'UNPAID',
    label: 'Unpaid',
    badgeClass: BADGE_CLASSES.UNPAID,
  };
}

/**
 * The band over each status in the Invoices list, in the server's order
 * (`groupBy=status`, INVOICE_GROUPS in the backend's invoice-list.ts):
 * unpaid work first, archived last. Archived invoices are listed only when
 * the Status filter asks for them, as the server leaves them out otherwise.
 */
export const INVOICE_STATUS_BANDS: Record<
  InvoiceStatus,
  { label: string; tone: NonNullable<ListBandProps['tone']> }
> = {
  DRAFT: { label: 'Draft', tone: 'neutral' },
  SENT: { label: 'Sent', tone: 'info' },
  PROCESSING: { label: 'Processing', tone: 'warning' },
  PAID: { label: 'Paid', tone: 'success' },
  ARCHIVED: { label: 'Archived', tone: 'neutral' },
};

export const INVOICE_STATUSES = Object.keys(
  INVOICE_STATUS_BANDS
) as InvoiceStatus[];

const bandOf = (invoice: InvoicePaymentFields) =>
  INVOICE_STATUS_BANDS[(invoice.status ?? 'DRAFT') as InvoiceStatus];

/**
 * One invoice's status in words, as a person at the booth needs it: a sent
 * invoice the customer has paid online says Paid, or Payment pending while
 * the provider confirms, before the stored status catches up.
 */
export function invoiceStatusLabel(invoice: InvoicePaymentFields): string {
  if (invoice.status === 'DRAFT' || invoice.status === 'ARCHIVED') {
    return bandOf(invoice).label;
  }
  const payment = resolveInvoicePaymentDisplay(invoice);
  return payment.kind === 'UNPAID' ? bandOf(invoice).label : payment.label;
}

/**
 * The colour of an invoice's status pill in the table, from the same
 * payment as its words: a sent invoice paid online is green before the
 * stored status catches up, and one the provider is still confirming amber.
 */
export function invoiceStatusTone(
  invoice: InvoicePaymentFields
): NonNullable<ListBandProps['tone']> {
  if (invoice.status === 'DRAFT' || invoice.status === 'ARCHIVED') {
    return bandOf(invoice).tone;
  }
  const payment = resolveInvoicePaymentDisplay(invoice);
  if (payment.kind === 'PAID') return 'success';
  if (payment.kind === 'PENDING') return 'warning';
  return bandOf(invoice).tone;
}
